(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define(['jquery', 'underscore', 'torso/modules/View', 'app/widget/modal/ModalBehavior', 'templates', 'app/utils/viewUtils'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var $ = require('jquery');
    var _ = require('underscore');
    var templates = {
      modalDialogTemplate: require('./modalDialogTemplate.hbs')
    };
    module.exports = factory($, _, require('backbone-torso/modules/View'), require('./ModalBehavior'), templates, require('./../../utils/viewUtils'));
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function ($, _, TorsoView, ModalBehavior, templates, viewUtils) {
  'use strict';

  /**
   * Modal Dialog Widget
   *
   * This is a basic Modal (that uses ModalBehavior) for dialogs. Based on user
   * input, either resolves or rejects the modal (either without a results object).
   *
   * Can be heavily customized (see documentation for initialize, below).  Additionally, you can
   * use the 'className' option to set the overall size of the modal:
   * 'brec-dialog--full', brec-dialog--big', 'brec-dialog--regular', or 'brec-dialog--small' (defaults to small).
   *
   * Can set attributes 'data-view-name'.
   *
   * @class ModalDialogWidget
   * @constructor
   */
  return TorsoView.extend({
    className: 'brec-dialog brec-dialog--small',
    template: templates.modalDialogTemplate,
    attributes: function() {
      return {
        'role' : 'dialog',
        'data-view-name' : this._dataViewName,
        'aria-labelledby': 'dialog-title',
        'aria-describedby': 'dialog-description',
        'data-test-modal': true
      };
    },

    _defaultHeaderKey: 'modalWidget.defaultHeaderKey',
    _defaultYesKey: 'modalWidget.defaultYesKey',
    _defaultNoKey: 'modalWidget.defaultNoKey',
    _defaultCloseKey: 'modalWidget.defaultCloseKey',
    _defaultCloseTitleKey: 'modalWidget.defaultCloseTitleKey',

    events: {
      'click [data-close]': 'cancel',
      'click [data-no]': 'cancel',
      'click [data-yes]': 'confirm'
    },

    behaviors: {
      modalBehavior: ModalBehavior
    },

    prepareFields: {
      showErrorSection: function() {
        return this.get('errorDescription') || this.get('errorDescriptionKey');
      }
    },

    /**
     * @method initialize
     * @override
     * @param options {Object} Options to configure this view.
     *   @param [options.dataViewName] {String} name for the data view
     *   @param [options.dataViewTestIdentifier] {String} unique identifier suffix to use for data-test attribute.
     *                                                    Attribute will be "data-test-modal-data-{{dataViewTestIdentifier}}".
     *   @param [options.headerKey=_defaultHeaderKey] {String} i18n key for the header
     *   @param [options.descriptionKey=null] {String} i18n key for the description
     *   @param [options.description=null] {String} text for the description
     *   @param [options.bottomDescriptionKey=null] {String} i18n key for the bottom description (when trackedAboveDescription = false)
     *   @param [options.bottomDescription=null] {String} text for the bottom description (when trackedAboveDescription = false)
     *   @param [options.yesKey=_defaultYesKey] {String} i18n key for the 'yes' button
     *   @param [options.yesClass=null] {String} additional classes for the 'yes' button
     *   @param [options.noKey=_defaultNoKey] {String} i18n key for the 'no' button
     *   @param [options.noClass=null] {String} additional classes for the 'no' button
     *   @param [options.footerClass=null] {String} additional classes for the footer
     *   @param [options.closeKey=_defaultCloseKey] {String} i18n key for the 'close' button
     *   @param [options.closeTitleKey=_defaultCloseTitleKey] {String} i18n key for the 'x' button's mouseover
     *   @param [options.alertOnly=false] {boolean} if true, displays close button only
     *   @param [options.hideClose=false] {boolean} if true, does not display the 'x' button
     *   @param [options.stackButtons=false] {boolean} if true, vertically stacks the 'yes' and 'no' buttons
     *   @param [options.noPrimary=false] {boolean} if true, the 'yes' button will not be styled 'primary'
     *   @param [options.trackedView=null] {view} optional embedded child view instance
     *   @param [options.trackedViewOptions=null] {Object} attachment options for tracked view
     *   @param [options.trackedAboveDescription=false] {boolean} if true, put the embedded child view above the description
     *   @param [options.yesEnabled=true] {boolean} if true, the yes button is enabled.  False it is disabled.
     *   @param [options.noEnabled=true] {boolean} if true, the no button is enabled.  False it is disabled.
     *   @param [options.errorDescription=null] {string} the raw string to use for the error description (when yesEnabled = false).
     *   @param [options.errorDescriptionKey=null] {string} the message key to use for the error description (when yesEnabled = false).
     */
    initialize: function(options) {
      options = options || {};
      this._dataViewName = options.dataViewName; // no default
      this.set('dataViewTestIdentifier', options.dataViewTestIdentifier); // no default
      viewUtils.updateViewElAttributes(this);

      this.set('headerKey', options.headerKey || this._defaultHeaderKey);
      this.set('descriptionKey', options.descriptionKey); // no default
      this.set('description', options.description); // no default
      this.set('bottomDescriptionKey', options.bottomDescriptionKey); // no default
      this.set('bottomDescription', options.bottomDescription); // no default
      this.set('yesKey', options.yesKey || this._defaultYesKey);
      this.set('yesClass', options.yesClass || '');
      this.set('noKey', options.noKey || this._defaultNoKey);
      this.set('noClass', options.noClass || '');
      this.set('footerClass', options.footerClass || '');
      this.set('closeKey', options.closeKey || this._defaultCloseKey);
      this.set('closeTitleKey', options.closeTitleKey || this._defaultCloseTitleKey);

      this.set('alertOnly', options.alertOnly || false);
      this.set('hideClose', options.hideClose || false);
      this.set('stackButtons', options.stackButtons || false);
      this.set('noPrimary', options.noPrimary || false);

      this.set('yesEnabled', _.isUndefined(options.yesEnabled) ? true : options.yesEnabled);
      this.set('noEnabled', _.isUndefined(options.noEnabled) ? true : options.noEnabled);
      this.set('errorDescription', options.errorDescription); // no default.
      this.set('errorDescriptionKey', options.errorDescriptionKey); // no default.

      this._trackedView = options.trackedView;
      this._trackedViewOptions = options.trackedViewOptions;
      this.set('trackedAboveDescription', options.trackedAboveDescription);

      _.bindAll(this, 'keyEscape');

      this.listenTo(this.viewState, 'change', this.render);
    },

    /**
     * @method _attached
     * @override
     */
    _attached: function() {
      $('body').on('keyup', this.keyEscape);
    },

    /**
     * @method _dispose
     * @override
     */
    _detached: function() {
      $('body').off('keyup', this.keyEscape);
    },

    /**
     * @method attachTrackedViews
     * @override
     */
    attachTrackedViews: function() {
      if (this._trackedView) {
        this.attachView('confirm-tracked-view', this._trackedView, this._trackedViewOptions);
      }
    },

    /**
     * This is a simple wrapper around this.resolveModal(), to clearly indicate a simple confirmation.
     *
     * @method confirm
     */
    confirm: function() {
      this.resolveModal();
    },

    /**
     * This is a simple wrapper around this.rejectModal(), to clearly indicate a simple cancelation.
     *
     * @method confirm
     */
    cancel: function() {
      this.rejectModal();
    },

    /**
     * Close the modal if ESC is pressed.
     *
     * @method keyEscape
     * @param event {Object} key press event object
     */
    keyEscape: function(event) {
      if (this.isTopActive()) {
        var code = event.keyCode || event.which;
        if (code === 27) {
          this.cancel();
          return false;
        }
      }
    }
  });
}));
